import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, useIntl, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { isMobile } from '../../constants/constants';
import moment from 'moment';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import { axiosCaller } from '../../utils/apiCaller';
import { SelectOption } from '../../types/selectOption.type';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatPrice } from '../../utils/react';
import '../Contact/Contact.scss';
import '../Offers/Offers.scss';
import ProductionProductCardGrid from './ProductionProductCardGrid';
import MikaFileManager, { MikaFile } from '../../components/MikaFileManager/MikaFileManager';

interface Props {
    history: any;
    location: any;
    productionActions: any;
    production: any;
    intl: any;
    permissions: any;
    buttonLoader: boolean;
    departments: SelectOption[];
    actions: any;
    loading: any;
}

const STATUS_VISUAL: Record<number, { key: string; color: string; bg: string }> = {
    2: { key: 'waiting',   color: '#3b82f6', bg: '#dbeafe' },
    3: { key: 'late',      color: '#ef4444', bg: '#fef2f2' },
    4: { key: 'inProcess', color: '#f59e0b', bg: '#fef3c7' },
    9: { key: 'mixed',     color: '#8b5cf6', bg: '#f3e8ff' },
};

const STATUS_BADGE_I18N: Record<number, { id: string; defaultMessage: string }> = {
    2: { id: 'production.dashboard.onHold',            defaultMessage: 'Në pritje' },
    3: { id: 'production.dashboard.lateProducts',      defaultMessage: 'Me vonesë' },
    4: { id: 'production.dashboard.inProcess',         defaultMessage: 'Në proces' },
    9: { id: 'production.products.status.inactive',    defaultMessage: 'Në proces jo aktiv' },
};

// ── Locale-aware month-browsing calendar header ──
interface CalendarHeaderProps {
    date: Date; changeYear: (y: number) => void; changeMonth: (m: number) => void;
    decreaseMonth: () => void; increaseMonth: () => void;
    prevMonthButtonDisabled: boolean; nextMonthButtonDisabled: boolean;
}
const CalendarHeader: React.FC<CalendarHeaderProps> = ({
    date, changeYear, changeMonth, decreaseMonth, increaseMonth,
    prevMonthButtonDisabled, nextMonthButtonDisabled
}) => {
    const intl = useIntl();
    const [showMonths, setShowMonths] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const year = date.getFullYear();
    const month = date.getMonth();

    const months = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(intl.locale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => {
            const name = fmt.format(new Date(2000, i, 1));
            return name.charAt(0).toUpperCase() + name.slice(1);
        });
    }, [intl.locale]);

    useEffect(() => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        if (!cal) return;
        const container = cal.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (showMonths) {
            cal.classList.add('month-picker-active');
        } else {
            cal.classList.remove('month-picker-active');
            if (container) container.style.minHeight = '';
        }
    }, [showMonths]);

    function openMonthPicker() {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (container) container.style.minHeight = container.offsetHeight + 'px';
        setShowMonths(true);
    }

    if (showMonths) return (
        <div ref={headerRef} className="dp-header dp-header--months">
            <div className="dp-year-nav">
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year - 1)}>&#8249;</button>
                <span className="dp-year-label">{year}</span>
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year + 1)}>&#8250;</button>
            </div>
            <div className="dp-month-grid">
                {months.map((m, i) => (
                    <button type="button" key={m}
                        className={`dp-month-btn${month === i ? ' active' : ''}`}
                        onClick={() => { changeMonth(i); setShowMonths(false); }}>
                        {m}
                    </button>
                ))}
            </div>
        </div>
    );
    return (
        <div ref={headerRef} className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>&#8249;</button>
            <button type="button" className="dp-month-year-btn" onClick={openMonthPicker}>
                {months[month]} {year} <span className="dp-chevron">&#9662;</span>
            </button>
            <button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>&#8250;</button>
        </div>
    );
};

const VIEW_STORAGE_KEY = 'productionProductsViewMode';

const ProductionProductsPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const weekdayNarrow = useCallback((d: string) => {
        const idx = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].indexOf(d);
        if (idx < 0) return d;
        const date = new Date(2024, 0, 7 + idx);
        return new Intl.DateTimeFormat(intl.locale, { weekday: 'narrow' }).format(date);
    }, [intl.locale]);

    const getStatusBadgeLabel = useCallback((overallStatus: number) => {
        const entry = STATUS_BADGE_I18N[overallStatus] || STATUS_BADGE_I18N[9];
        return fm(entry.id, entry.defaultMessage);
    }, [fm]);

    const getMeasureLabel = useCallback((measureStatus: number) => {
        return measureStatus === 2
            ? fm('production.detail.measureFinal', 'Matje finale')
            : fm('production.detail.measureInitial', 'Matje fillestare');
    }, [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        deptIds: { default: '', type: 'string' },
        statuses: { default: '', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        dateMode: { default: 'end', type: 'string' },
        sortField: { default: 'end', type: 'string' },
        sortDir: { default: 'asc', type: 'string' },
        category: { default: '', type: 'string' },
        workState: { default: '', type: 'string' },
    });

    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const selectedDepartmentIds: number[] = filters.deptIds ? filters.deptIds.split(',').map(Number) : [];
    const selectedStatuses: number[] = filters.statuses ? filters.statuses.split(',').map(Number) : [];
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const sortField = filters.sortField;
    const sortDir = filters.sortDir;

    const setCurrentPage = (p: number) => setFilter('page', p);
    const setSelectedDepartmentIds = (updater: number[] | ((prev: number[]) => number[])) => {
        const next = typeof updater === 'function' ? updater(selectedDepartmentIds) : updater;
        setFilters({ deptIds: next.join(','), page: 1 });
    };
    const setSelectedStatuses = (updater: number[] | ((prev: number[]) => number[])) => {
        const next = typeof updater === 'function' ? updater(selectedStatuses) : updater;
        setFilters({ statuses: next.join(','), page: 1 });
    };
    const setDateRange = (r: DateRange | null) => {
        setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    };
    const setDateMode = (mode: 'start' | 'end' | 'overlap') => {
        setFilters({ dateMode: mode, page: 1 });
    };

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const [statsData, setStatsData] = useState<any>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [productStartDate, setProductStartDate] = useState('');
    const [productEndDate, setProductEndDate] = useState('');
    const [calendarComment, setCalendarComment] = useState('');
    const [blueprintLoading, setBlueprintLoading] = useState(false);

    const [productsWithStatuses, setProductsWithStatuses] = useState<any[]>([]);
    const [totalPrice, setTotalPrice] = useState(0);

    const activeCategory = (filters.category === 'production' || filters.category === 'activity') ? filters.category : '';
    // Sub-filter within a category lens: '' (all) | 'active' (Në proces) | 'idle' (Jo aktiv).
    const activeWorkState = (filters.workState === 'active' || filters.workState === 'idle') ? filters.workState : '';

    const loadStats = () => {
        props.productionActions.getProductionDashboardStats(activeCategory).then((res: any) => {
            if (res) setStatsData(res);
        });
    };

    useEffect(() => {
        props.actions.getAllDepartments();
    }, []);

    useEffect(() => {
        fetchData();
        loadStats();
    }, [props.location.search]);

    const fetchData = () => {
        props.productionActions.fetchProductPrices(
            null, dateRange?.from || null, dateRange?.to || null,
            0, searchTerm, null, null, sortField,
            dateRange ? filters.dateMode : null,
            null, currentPage, perPage, undefined,
            {
                departmentIds: selectedDepartmentIds.length > 0 ? selectedDepartmentIds : undefined,
                overallStatuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
                category: activeCategory || undefined,
                workState: activeWorkState || undefined,
            }
        );
    };

    const handleSearch = (term: string) => {
        replaceFilters({ search: term, page: 1 });
    };

    useEffect(() => {
        const transformed = (props.production.productPrices || []).map((p: any) => {
            const visual = STATUS_VISUAL[p.overallStatus] || STATUS_VISUAL[9];
            return {
                productId: p.id,
                clientId: p.client?.id,
                clientName: p.client?.name,
                productName: p.designation?.name,
                productCode: p.type,
                projectName: p.project?.name,
                projectId: p.project?.id,
                start: p.start,
                end: p.end,
                quantity: p.quantity ? `${parseFloat(p.quantity).toFixed(2)}${p.designation?.unit?.name || ''}` : null,
                totalPrice: p.discountedPrice || p.totalPrice,
                measureStatus: p.measureStatus,
                measureDone: getMeasureLabel(p.measureStatus),
                calendarComment: p.calendarComment,
                overallStatus: visual.key,
                overallStatusId: p.overallStatus,
                completedProcesses: p.completedProcesses,
                totalProcesses: p.totalProcesses,
                productionStartedAt: p.productionStartedAt,
                statusLabel: getStatusBadgeLabel(p.overallStatus),
                statusColor: visual.color,
                statusBg: visual.bg,
            };
        });
        setProductsWithStatuses(transformed);
        setTotalPrice(Number(props.production?.productPricesTotalPrice) || 0);
    }, [props.production.productPrices, props.production.productPricesTotalPrice, intl.locale]);

    const dashboardStats = useMemo(() => {
        const s = statsData;
        return [
            { key: 'total',      label: fm('production.products.strip.total', 'Të gjitha'),         value: s?.total ?? 0,                     icon: 'users' },
            { key: 'waiting',    label: fm('production.dashboard.onHold', 'Në pritje'),              value: s?.waiting ?? '—',                 icon: 'alert' },
            { key: 'activity',   label: fm('production.products.strip.activity', 'Aktivitete'),      value: s?.byCategory?.activity ?? '—',    icon: 'agent' },
            { key: 'production', label: fm('production.products.strip.production', 'Në proces të prodhimit'), value: s?.byCategory?.production ?? '—', icon: 'box' },
            { key: 'departments',label: fm('production.dashboard.departments', 'Departamentet'),    value: selectedDepartmentIds.length > 0 ? selectedDepartmentIds.length : (props.departments || []).length, icon: 'layers' },
        ];
    }, [statsData, props.departments, selectedDepartmentIds, fm]);

    const [deptPopoverOpen, setDeptPopoverOpen] = useState(false);
    const deptRef = useRef<HTMLDivElement>(null);
    const [deptPopoverPos, setDeptPopoverPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (deptRef.current && !deptRef.current.contains(e.target as Node)) setDeptPopoverOpen(false);
        };
        if (deptPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [deptPopoverOpen]);

    const toggleDeptFilter = (deptId: number) => {
        const next = selectedDepartmentIds.includes(deptId) ? selectedDepartmentIds.filter(id => id !== deptId) : [...selectedDepartmentIds, deptId];
        setFilters({ deptIds: next.join(','), page: 1 });
    };

    // The strip cards are "lenses" — each picks a primary view. Lenses are
    // mutually exclusive; departments is an orthogonal overlay (popover).
    const handleStripClick = (key: string) => {
        if (key === 'total') {
            // Clear everything — back to "Të gjitha" (all approved products).
            setFilters({ statuses: '', deptIds: '', category: '', workState: '', page: 1 });
            setDeptPopoverOpen(false);
            return;
        }
        if (key === 'waiting') {
            // "Në pritje" lens — backlog (overall_status = 2, nothing started).
            // No category/sub-filter; re-click toggles back to all.
            const isActive = !activeCategory && selectedStatuses.length === 1 && selectedStatuses[0] === 2;
            setFilters({ category: '', workState: '', statuses: isActive ? '' : '2', page: 1 });
            return;
        }
        if (key === 'activity' || key === 'production') {
            // Category lens. Re-click deselects; switching resets the sub-filter
            // so you land on "Të gjitha" within the new lens.
            const next = activeCategory === key ? '' : key;
            setFilters({ category: next, statuses: '', workState: '', page: 1 });
            return;
        }
        if (key === 'departments') {
            if (!deptPopoverOpen && stripRef.current) {
                const cards = stripRef.current.querySelectorAll('.cds-card');
                const card = cards[cards.length - 1];
                if (card) {
                    const rect = card.getBoundingClientRect();
                    setDeptPopoverPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
                }
            }
            setDeptPopoverOpen(!deptPopoverOpen);
        }
    };

    // Sub-filter (radio) inside a category lens. '' = Të gjitha (whole lens),
    // 'active' = Në proces (work happening now), 'idle' = Jo aktiv (sitting).
    const handleSubFilter = (state: string) => {
        setFilters({ workState: activeWorkState === state ? '' : state, page: 1 });
    };

    const activeStripKeys = useMemo(() => {
        const keys: string[] = [];
        if (activeCategory === 'activity') keys.push('activity');
        else if (activeCategory === 'production') keys.push('production');
        else if (selectedStatuses.length === 1 && selectedStatuses[0] === 2) keys.push('waiting');
        if (selectedDepartmentIds.length > 0) keys.push('departments');
        // Default highlight only when truly nothing is filtered (the late badge
        // sets statuses=3 with no lens — in that case nothing in the strip lights up).
        if (keys.length === 0 && selectedStatuses.length === 0 && !activeCategory) keys.push('total');
        return keys;
    }, [selectedStatuses, selectedDepartmentIds, activeCategory]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'clientName',
            title: fm('production.detail.client', 'Klienti'),
            sortable: true,
            render: (val: any) => val || '\u2014'
        },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), hideOnMobile: true },
        {
            key: 'productName',
            title: fm('production.board.productCol', 'Produkti'),
            render: (val: any, row: any) => row.productCode ? `${val} (${row.productCode})` : val || '\u2014'
        },
        {
            key: 'start',
            title: fm('production.detail.start', 'Fillimi'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val ? moment(val).format('DD-MM-YYYY') : '\u2014'
        },
        {
            key: 'end',
            title: fm('production.detail.end', 'Mbarimi'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val ? moment(val).format('DD-MM-YYYY') : '\u2014'
        },
        {
            key: 'quantity',
            title: fm('production.detail.quantity', 'Sasia'),
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'totalPrice',
            title: fm('production.detail.value', 'Vlera'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val ? formatPrice(val, '\u20AC') : '\u2014'
        },
        {
            key: 'measureDone',
            title: fm('production.products.column.measure', 'Matja'),
            hideOnMobile: true,
            render: (val: any, row: any) => {
                const isFinale = row.measureStatus === 2;
                return <span style={{
                    color: isFinale ? '#22c55e' : '#f59e0b',
                    background: isFinale ? '#dcfce7' : '#fef3c7',
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500
                }}>{val || '\u2014'}</span>;
            }
        },
        {
            key: 'statusLabel',
            title: fm('production.products.column.status', 'Statusi'),
            render: (_: any, row: any) => {
                return <span style={{
                    color: row.statusColor || '#94a3b8',
                    background: row.statusBg || '#f1f5f9',
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500, display: 'inline-block'
                }}>{row.statusLabel || '\u2014'}</span>;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const openModal = (row: any) => {
        setBlueprintLoading(true);
        props.productionActions.getBlueprintFiles(row.productId).then(() => setBlueprintLoading(false)).catch(() => setBlueprintLoading(false));
        setSelectedProduct(row);
        setProductStartDate('');
        setProductEndDate('');
        setCalendarComment('');
    };

    const closeModal = () => {
        setSelectedProduct(null);
    };

    const saveProductDates = () => {
        if (productStartDate || productEndDate) {
            if (!calendarComment) {
                toast.info(fm('production.products.toast.dateCommentRequired', 'Ju lutem vendosni një koment për ndryshime të datave!'));
                return;
            }
        }
        if (productStartDate && productEndDate && new Date(productStartDate).getTime() > new Date(productEndDate).getTime()) {
            toast.error(fm('production.toast.endBeforeStart', 'Data e mbarimit nuk mund të jetë para fillimit!'));
            return;
        }
        const existingComment = selectedProduct?.calendarComment || '';
        const timestamp = moment().format('DD-MM-YYYY HH:mm');
        const fullComment = calendarComment
            ? (existingComment ? `${existingComment}\n[${timestamp}] ${calendarComment}` : `[${timestamp}] ${calendarComment}`)
            : existingComment;
        const payload = {
            productId: Number(selectedProduct?.productId),
            start: productStartDate ? moment(productStartDate).format('YYYY-MM-DD') : moment(selectedProduct?.start).format('YYYY-MM-DD'),
            end: productEndDate ? moment(productEndDate).format('YYYY-MM-DD') : moment(selectedProduct?.end).format('YYYY-MM-DD'),
            calendarComment: fullComment,
        };
        props.productionActions.updateProductDates(payload).then((res: any) => {
            if (res?.data?.data) {
                toast.success(fm('production.products.toast.dateEdited', 'Data është edituar'));
                closeModal();
                fetchData();
            } else {
                toast.error(fm('production.expansion.datesTaken', 'Këto data janë të zëna!'));
            }
        });
    };

    const deleteBlueprintFile = (fileId: number) => {
        setBlueprintLoading(true);
        props.productionActions.deleteBlueprintFile(fileId).then(() => {
            props.productionActions.getBlueprintFiles(selectedProduct?.productId).then(() => setBlueprintLoading(false));
        });
    };

    const deleteBlueprintFiles = async (fileIds: number[]) => {
        setBlueprintLoading(true);
        for (const id of fileIds) {
            await props.productionActions.deleteBlueprintFile(id);
        }
        await props.productionActions.getBlueprintFiles(selectedProduct?.productId);
        setBlueprintLoading(false);
    };

    const blueprintMikaFiles: MikaFile[] = (props.production?.blueprintFiles || []).map((f: any) => ({
        id: f.id,
        name: f.nameOfBlueprint || 'file',
        url: f.blueprint,
    }));

    const pagination = props.production?.productPricesPagination;

    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'clientName',  title: fm('production.detail.client', 'Klienti') },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti') },
        { key: 'productName', title: fm('production.board.productCol', 'Produkti'), render: (r) => r.productCode ? `${r.productName} (${r.productCode})` : r.productName || '' },
        { key: 'start',       title: fm('production.detail.start', 'Fillimi'),       render: (r) => r.start ? moment(r.start).format('DD-MM-YYYY') : '' },
        { key: 'end',         title: fm('production.detail.end', 'Mbarimi'),         render: (r) => r.end ? moment(r.end).format('DD-MM-YYYY') : '' },
        { key: 'quantity',    title: fm('production.detail.quantity', 'Sasia') },
        { key: 'totalPrice',  title: fm('production.detail.value', 'Vlera'),         render: (r) => r.totalPrice ? formatPrice(r.totalPrice) : '' },
        { key: 'measureDone', title: fm('production.products.column.measure', 'Matja') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async (): Promise<any[]> => {
        let qs: string[] = ['per_page=0', 'done=0', 'active=1'];
        if (selectedDepartmentIds.length > 0) selectedDepartmentIds.forEach(id => qs.push(`departmentIds[]=${id}`));
        if (selectedStatuses.length > 0) selectedStatuses.forEach(s => qs.push(`overallStatuses[]=${s}`));
        if (dateRange?.from && dateRange?.to) { qs.push(`start=${dateRange.from}`); qs.push(`end=${dateRange.to}`); qs.push(`filterDatesBy=${filters.dateMode}`); }
        if (searchTerm) qs.push(`name=${encodeURIComponent(searchTerm)}`);
        const res = await axiosCaller('GET', `products/view-price?${qs.join('&')}`);
        const raw = res?.data?.data || [];
        return raw.map((p: any) => ({
            clientName: p.client?.name,
            productName: p.designation?.name,
            productCode: p.type,
            projectName: p.project?.name,
            start: p.start,
            end: p.end,
            quantity: p.quantity ? `${parseFloat(p.quantity).toFixed(2)}${p.designation?.unit?.name || ''}` : '',
            totalPrice: p.discountedPrice || p.totalPrice,
            measureDone: getMeasureLabel(p.measureStatus),
        }));
    };

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>,
            tooltip: fm('production.products.info', 'Info'),
            onClick: openModal
        }
    ];

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={<>{fm('production.products.title', 'Produktet në prodhim')} {props.permissions?.fullPrivileges && <span style={{ fontSize: 18, fontWeight: 700, color: '#22c55e', marginLeft: 12 }}>{formatPrice(totalPrice)}€</span>}{statsData?.late > 0 && <button type="button" onClick={() => setFilters({ category: '', workState: '', statuses: (selectedStatuses.length === 1 && selectedStatuses[0] === 3) ? '' : '3', page: 1 })} style={{ marginLeft: 12, height: 26, padding: '0 10px', border: 'none', borderRadius: 13, background: (selectedStatuses.length === 1 && selectedStatuses[0] === 3) ? '#ef4444' : '#fae7e7', color: (selectedStatuses.length === 1 && selectedStatuses[0] === 3) ? '#fff' : '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 700, verticalAlign: 'middle' }}>{statsData.late} {fm('production.products.lateBadge', 'vonuar')}</button>}</>}
            />

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.products.searchPlaceholder', 'Kërko sipas klientit...')}
                        value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {dateRange && (
                        <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #d0dbe8' }}>
                            {(['start', 'end', 'overlap'] as const).map((mode, idx) => {
                                const isActive = filters.dateMode === mode;
                                const labelKey = mode === 'start' ? 'production.products.dateMode.start'
                                    : mode === 'end' ? 'production.products.dateMode.end'
                                    : 'production.products.dateMode.overlap';
                                const tooltipKey = mode === 'start' ? 'production.products.dateMode.startTooltip'
                                    : mode === 'end' ? 'production.products.dateMode.endTooltip'
                                    : 'production.products.dateMode.overlapTooltip';
                                const defaultLabel = mode === 'start' ? 'Start' : mode === 'end' ? 'End' : 'Active';
                                const defaultTooltip = mode === 'start' ? 'Products starting in range'
                                    : mode === 'end' ? 'Products ending in range'
                                    : 'Products active in range';
                                return (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setDateMode(mode)}
                                        title={fm(tooltipKey, defaultTooltip)}
                                        style={{
                                            height: 32,
                                            padding: '0 12px',
                                            border: 'none',
                                            background: isActive ? '#1e3a5f' : '#f1f5f9',
                                            color: isActive ? '#fff' : '#64748b',
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            letterSpacing: '0.2px',
                                            transition: 'all 0.15s ease',
                                            borderRadius: idx === 0 ? '5px 0 0 5px' : (idx === 2 ? '0 5px 5px 0' : 0),
                                        }}
                                    >
                                        {fm(labelKey, defaultLabel)}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('production.products.exportFilename', 'produktet_ne_prodhim')} title={fm('production.products.title', 'Produktet në prodhim')} companyProfile={(props as any).companyProfile}
                        sumColumn={{ getValue: (row) => row.totalPrice ? Number(row.totalPrice) : 0, label: fm('production.products.exportSumLabel', 'Totali:'), suffix: '\u20AC' }}
                    />
                </div>
            </div>

            {/* Dashboard strip */}
            <div ref={stripRef} style={{ position: 'relative' }}>
                <ContactDashboardStrip
                    stats={dashboardStats}
                    activeFilter={activeStripKeys}
                    onFilterClick={handleStripClick}
                />
            </div>
            {deptPopoverOpen && (
                <div ref={deptRef} className="clp-city-popover" style={{ top: deptPopoverPos.top, left: deptPopoverPos.left, transform: 'translateX(-50%)' }}>
                    <div className="clp-city-header"><FormattedMessage id="production.dashboard.departments" defaultMessage="Departamentet" /></div>
                    {(props.departments || []).map((dept: any) => (
                        <button
                            key={dept.value}
                            className={`clp-city-row ${selectedDepartmentIds.includes(dept.value) ? 'clp-city-row-active' : ''}`}
                            onClick={() => toggleDeptFilter(dept.value)}
                            type="button"
                        >
                            <span className="clp-city-name">{dept.label}</span>
                            {selectedDepartmentIds.includes(dept.value) && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Contextual sub-filter — radio pills shown only inside a category lens.
                Counts: "Të gjitha" = the lens partition size; the others are
                category-scoped (backend re-scopes inProcess/mixed when category is set). */}
            {activeCategory && (
                <div className="clp-active-filter-bar" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                        {fm('production.products.subfilter.label', 'Filtro sipas statusit:')}
                    </span>
                    <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden' }}>
                        {(() => {
                            const lensTotal = statsData?.byCategory?.[activeCategory];
                            const inProc = statsData?.inProcess;
                            // Idle = lens minus actively-in-process (the lens splits cleanly into the two).
                            const idle = (lensTotal != null && inProc != null) ? lensTotal - inProc : undefined;
                            return [
                                { val: '', label: fm('production.products.strip.total', 'Të gjitha'), count: lensTotal },
                                { val: 'active', label: fm('production.dashboard.inProcess', 'Në proces'), count: inProc },
                                { val: 'idle', label: fm('production.status.nonActive', 'Jo aktiv'), count: idle },
                            ];
                        })().map((sf, idx, arr) => {
                            const active = activeWorkState === sf.val;
                            return (
                                <button
                                    key={sf.val || 'all'}
                                    type="button"
                                    onClick={() => handleSubFilter(sf.val)}
                                    style={{
                                        height: 30, padding: '0 14px', border: 'none', cursor: 'pointer',
                                        background: active ? '#1e3a5f' : '#f1f5f9',
                                        color: active ? '#fff' : '#64748b',
                                        fontSize: 12, fontWeight: 600, transition: 'all 0.15s ease',
                                        borderRadius: idx === 0 ? '6px 0 0 6px' : (idx === arr.length - 1 ? '0 6px 6px 0' : 0),
                                    }}
                                >
                                    {sf.label}{sf.count != null ? ` ${sf.count}` : ''}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Active department chips (orthogonal overlay) */}
            {selectedDepartmentIds.length > 0 && (
                <div className="clp-active-filter-bar" style={{ flexWrap: 'wrap', gap: 6 }}>
                    {selectedDepartmentIds.map(id => {
                        const dept = (props.departments || []).find((d: any) => d.value === id);
                        return dept ? (
                            <div key={`dept-${id}`} className="clp-active-filter-chip">
                                <span>{dept.label}</span>
                                <button className="clp-active-filter-clear" onClick={() => { const next = selectedDepartmentIds.filter(v => v !== id); setFilters({ deptIds: next.join(','), page: 1 }); }} type="button">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                        ) : null;
                    })}
                </div>
            )}

            {effectiveView === 'table'
                ? <MikaTable
                    columns={columns}
                    data={productsWithStatuses}
                    actions={tableActions}
                    loading={props.loading}
                    onRowClick={openModal}
                />
                : <ProductionProductCardGrid data={productsWithStatuses} onEdit={openModal} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setFilter('page', p)} onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('production.board.subtitleProducts', 'produkte')}
            />

            {/* Product detail modal */}
            {selectedProduct && (
                <div className="agr-modal-overlay" onClick={closeModal}>
                    <div className="agr-modal prod-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="agr-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{selectedProduct.productName}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: selectedProduct.statusBg, color: selectedProduct.statusColor }}>{selectedProduct.statusLabel}</span>
                                <button onClick={closeModal} type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>&times;</button>
                            </div>
                        </div>
                        <div className="agr-modal-body" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                            {/* Basic info */}
                            <div style={{ marginBottom: 12, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                                <div><strong><FormattedMessage id="production.detail.client" defaultMessage="Klienti" />:</strong> {selectedProduct.clientName}</div>
                                <div><strong><FormattedMessage id="production.detail.project" defaultMessage="Projekti" />:</strong> {selectedProduct.projectName}</div>
                                {selectedProduct.productCode && <div><strong><FormattedMessage id="production.products.modal.code" defaultMessage="Kodi" />:</strong> {selectedProduct.productCode}</div>}
                                {selectedProduct.quantity && <div><strong><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" />:</strong> {selectedProduct.quantity}</div>}
                                {selectedProduct.totalPrice && <div><strong><FormattedMessage id="production.detail.value" defaultMessage="Vlera" />:</strong> {formatPrice(selectedProduct.totalPrice, '€')}</div>}
                            </div>

                            {/* Progress bar */}
                            {selectedProduct.totalProcesses > 0 && (() => {
                                const completed = selectedProduct.completedProcesses || 0;
                                const total = selectedProduct.totalProcesses;
                                const pct = Math.round((completed / total) * 100);
                                const barColor = pct === 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#f59e0b';
                                return (
                                    <div style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}><FormattedMessage id="production.detail.progress" defaultMessage="Progresi" /></span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: barColor }}>{completed}/{total} ({pct}%)</span>
                                        </div>
                                        <div style={{ width: '100%', height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.3s ease' }} />
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Production timeline */}
                            {selectedProduct.productionStartedAt && (() => {
                                const parts = selectedProduct.productionStartedAt.split(/[-/ ]/);
                                let startDate: Date | null = null;
                                if (parts.length >= 3) {
                                    startDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                }
                                const days = (startDate && !isNaN(startDate.getTime()))
                                    ? Math.floor((Date.now() - startDate.getTime()) / 86400000)
                                    : 0;
                                return (
                                    <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                        <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 500 }}>
                                            <FormattedMessage id="production.detail.inProductionSince" defaultMessage="Në prodhim që nga: {date}" values={{ date: selectedProduct.productionStartedAt }} />
                                            {days > 0 && <> <FormattedMessage id="production.products.daysInProduction" defaultMessage="({days, plural, one {# ditë} other {# ditë}})" values={{ days }} /></>}
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Date fields */}
                            {props.permissions?.fullPrivileges ? (
                                <div className="prod-modal-dates">
                                    <div className="prod-modal-date-field">
                                        <label><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></label>
                                        <DatePicker
                                            selected={productStartDate ? new Date(productStartDate) : (selectedProduct.start ? moment(selectedProduct.start).toDate() : null)}
                                            onChange={(date: Date | null) => setProductStartDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                            className="prod-modal-input"
                                            calendarClassName="modern-datepicker-calendar"
                                            isClearable
                                            popperPlacement="bottom-start"
                                            popperModifiers={[{ name: 'offset', options: { offset: [0, 4] } }, { name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                            formatWeekDay={weekdayNarrow}
                                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                            popperContainer={({ children }: any) => <div style={{ position: 'relative', zIndex: 1100 }}>{children}</div>}
                                        />
                                    </div>
                                    <div className="prod-modal-date-field">
                                        <label><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></label>
                                        <DatePicker
                                            selected={productEndDate ? new Date(productEndDate) : (selectedProduct.end ? moment(selectedProduct.end).toDate() : null)}
                                            onChange={(date: Date | null) => setProductEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                            className="prod-modal-input"
                                            calendarClassName="modern-datepicker-calendar"
                                            isClearable
                                            popperPlacement="bottom-end"
                                            popperModifiers={[{ name: 'offset', options: { offset: [0, 4] } }, { name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                            formatWeekDay={weekdayNarrow}
                                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                            popperContainer={({ children }: any) => <div style={{ position: 'relative', zIndex: 1100 }}>{children}</div>}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div style={{ marginBottom: 10, fontSize: 13, color: '#475569' }}>
                                    <div><strong><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" />:</strong> {selectedProduct.start ? moment(selectedProduct.start).format('DD-MM-YYYY') : '\u2014'}</div>
                                    <div><strong><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" />:</strong> {selectedProduct.end ? moment(selectedProduct.end).format('DD-MM-YYYY') : '\u2014'}</div>
                                </div>
                            )}

                            {/* Comment history (read-only) */}
                            {selectedProduct.calendarComment && (
                                <div style={{ marginBottom: 10 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}><FormattedMessage id="production.products.modal.previousComments" defaultMessage="Komentet e mëparshme" /></label>
                                    <div style={{ padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#475569', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 100, overflowY: 'auto' }}>
                                        {selectedProduct.calendarComment}
                                    </div>
                                </div>
                            )}

                            {/* New comment input */}
                            {props.permissions?.fullPrivileges && (
                                <div style={{ marginBottom: 10 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}><FormattedMessage id="production.products.modal.newDateComment" defaultMessage="Koment i ri për ndryshim të datave" /></label>
                                    <textarea value={calendarComment}
                                        onChange={e => setCalendarComment(e.target.value)}
                                        placeholder={fm('production.statusForm.writeReason', 'Shkruaj arsyen...')}
                                        rows={2}
                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0dbe8', borderRadius: 6, fontSize: 12, resize: 'none', outline: 'none', fontFamily: 'inherit' }} />
                                </div>
                            )}

                            {/* Blueprint files via MikaFileManager */}
                            <div style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></label>
                                <MikaFileManager
                                    files={blueprintMikaFiles}
                                    onDelete={props.permissions?.fullPrivileges ? deleteBlueprintFile : undefined}
                                    onBulkDelete={props.permissions?.fullPrivileges ? deleteBlueprintFiles : undefined}
                                    loading={blueprintLoading}
                                />
                            </div>
                        </div>

                        {props.permissions?.fullPrivileges && !props.permissions?.observer && (
                            <div className="agr-modal-actions">
                                <button className="agr-btn-secondary" onClick={closeModal} type="button"><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                                <button className="agr-btn-primary" onClick={saveProductDates} type="button"><FormattedMessage id="production.detail.saveDates" defaultMessage="Ruaj datat" /></button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        production: state.production,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(injectIntl(ProductionProductsPage as any)));
